import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { AUDIT_ACTIONS } from "@/lib/constants";
import { fmtDateTime } from "@/lib/format";

export const actionLabel = (a) => AUDIT_ACTIONS.find((x) => x.value === a)?.label || a;

export const AuditEntry = ({ e, showBooking }) => (
  <li className="border-l-2 border-sky-300 pl-3" data-testid={`audit-entry-${e.id}`}>
    <div className="flex flex-wrap items-baseline gap-x-2 text-xs text-slate-500">
      <span className="font-mono">{fmtDateTime(e.created_at)}</span>
      <span>· Edited by <span className="font-semibold text-slate-800">{e.user_name}</span></span>
      <span className="rounded bg-slate-100 px-1.5 py-0.5 font-medium text-slate-600">{actionLabel(e.action)}</span>
      {showBooking && <span className="font-mono text-sky-700">{e.job_number} · {e.customer_name}</span>}
    </div>
    {e.summary && <p className="mt-1 text-sm text-slate-700">{e.summary}</p>}
    {e.changes?.length > 0 && (
      <ul className="mt-1.5 space-y-1">
        {e.changes.map((c, i) => (
          <li key={i} className="text-sm">
            <span className="font-medium text-slate-800">{c.label}</span>
            <div className="ml-2 text-xs"><span className="text-slate-500">Previous:</span> <span className="text-rose-700 line-through decoration-rose-300">{c.old}</span> <span className="mx-1 text-slate-400">→</span> <span className="text-slate-500">New:</span> <span className="font-semibold text-emerald-800">{c.new}</span></div>
          </li>
        ))}
      </ul>
    )}
  </li>
);

export const HistoryDialog = ({ booking, onClose }) => {
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get(`/bookings/${booking.id}/history`).then((r) => setEntries(r.data)).catch((e) => setError(formatApiError(e)));
  }, [booking.id]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl" data-testid="history-dialog">
        <DialogHeader>
          <DialogTitle>Edit history – {booking.job_number}</DialogTitle>
          <DialogDescription>Read-only record of every change to this booking. Entries cannot be edited or deleted.</DialogDescription>
        </DialogHeader>
        {error && <p className="text-sm text-rose-600">{error}</p>}
        {!entries && !error && <Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" />}
        {entries?.length === 0 && <p className="text-sm text-slate-500" data-testid="history-empty">No history recorded yet.</p>}
        <ul className="space-y-4" data-testid="history-list">{entries?.map((e) => <AuditEntry key={e.id} e={e} />)}</ul>
      </DialogContent>
    </Dialog>
  );
};
